import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { ownerFinanceImportApi } from "../../../../api/ownerFinanceImport";

import { formatApiError } from "../../../../api/client";

const money = (value, currency = "") => {
  const number = Number(value || 0);
  try {
    return new Intl.NumberFormat("es-AR", {
      style: currency ? "currency" : "decimal",
      currency: currency || undefined,
      maximumFractionDigits: 2,
    }).format(number);
  } catch {
    return `${number.toLocaleString("es-AR")} ${currency}`.trim();
  }
};

const dateLabel = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("es-AR");
};

function Metric({ label, value, tone = "default" }) {
  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1.2, minWidth: 128, flex: "1 1 128px", bgcolor: tone === "warn" ? "warning.50" : "background.paper" }}>
      <Typography sx={{ color: "text.secondary", fontSize: ".72rem", fontWeight: 850, textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</Typography>
      <Typography sx={{ mt: .2, fontSize: "1.18rem", fontWeight: 1000 }}>{value}</Typography>
    </Paper>
  );
}

export default function OwnerMovementImportDialog({ open, onClose, onCommitted }) {
  const inputRef = React.useRef(null);
  const [file, setFile] = React.useState(null);
  const [importId, setImportId] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setFile(null);
    setImportId("");
    setPreview(null);
    setResult(null);
    setError("");
    setBusy(false);
  }, [open]);

  const readFile = async (selected) => {
    if (!selected) return;
    setFile(selected);
    setPreview(null);
    setResult(null);
    setImportId("");
    setBusy(true);
    setError("");
    try {
      const draft = await ownerFinanceImportApi.create(selected);
      const checked = await ownerFinanceImportApi.preview(draft.importId);
      setImportId(draft.importId);
      setPreview(checked.preview);
    } catch (err) {
      setError(formatApiError(err, "No pude leer esta importación."));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!importId || !preview) return;
    setBusy(true);
    setError("");
    try {
      const response = await ownerFinanceImportApi.commit(importId);
      setResult(response.result);
      await onCommitted?.();
    } catch (err) {
      setError(formatApiError(err, "No pude confirmar los movimientos."));
    } finally {
      setBusy(false);
    }
  };

  const summary = preview?.summary || {};
  const readyCount = Number(summary.ready ?? 0);
  const duplicateCount = Number(summary.duplicates ?? 0);
  const blockingErrorCount = Number(summary.blockingErrors ?? 0);
  const canCommit = Boolean(importId) && Boolean(preview) && !result && !busy && blockingErrorCount === 0 && readyCount > 0;

  const commitState = React.useMemo(() => {
    if (!preview || result) return null;
    if (blockingErrorCount > 0) {
      return {
        severity: "error",
        title: "La importación necesita correcciones",
        detail: `${blockingErrorCount} fila${blockingErrorCount === 1 ? "" : "s"} bloquean la confirmación. Revisá el detalle antes de importar.`,
      };
    }
    if (readyCount <= 0 && duplicateCount > 0) {
      return {
        severity: "info",
        title: "Este historial ya está importado",
        detail: `${duplicateCount} movimiento${duplicateCount === 1 ? "" : "s"} ya existen. No hace falta volver a confirmarlos.`,
      };
    }
    if (readyCount <= 0) {
      return {
        severity: "warning",
        title: "No hay movimientos nuevos para confirmar",
        detail: "Elegí otro archivo o revisá la preparación del historial.",
      };
    }
    return {
      severity: "success",
      title: `${readyCount} movimientos listos para importar`,
      detail: "El preview pasó las validaciones. La confirmación recién ahora escribirá cuentas y movimientos.",
    };
  }, [preview, result, blockingErrorCount, readyCount, duplicateCount]);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography sx={{ fontWeight: 1000, fontSize: "1.2rem" }}>Importar movimientos históricos</Typography>
            <Typography sx={{ mt: .3, color: "text.secondary", fontSize: ".82rem", fontWeight: 700, maxWidth: 760 }}>
              Primero leemos y validamos. Nada toca tus saldos hasta que confirmes el preview. Las transferencias internas no se cuentan como ingreso ni gasto.
            </Typography>
          </Box>
          <Chip size="small" label="OWNER · PREVIEW → COMMIT" sx={{ fontWeight: 950 }} />
        </Stack>
      </DialogTitle>
      {busy ? <LinearProgress /> : null}
      <DialogContent dividers>
        <Stack spacing={1.6}>
          {!preview && !result ? (
            <Paper
              variant="outlined"
              onClick={() => !busy && inputRef.current?.click()}
              sx={{ p: 4, textAlign: "center", cursor: busy ? "default" : "pointer", borderStyle: "dashed", bgcolor: "action.hover" }}
            >
              <UploadFileRoundedIcon sx={{ fontSize: 42, color: "text.secondary" }} />
              <Typography sx={{ mt: 1, fontWeight: 1000 }}>Subí el XLSX limpio de movimientos</Typography>
              <Typography sx={{ mt: .4, color: "text.secondary", fontSize: ".82rem", fontWeight: 700 }}>
                CSV/XLSX · el parser es el mismo que usa el importador estable de WebProfit.
              </Typography>
              <Button
                sx={{ mt: 1.5, textTransform: "none", fontWeight: 950 }}
                variant="contained"
                disabled={busy}
                onClick={(event) => {
                  event.stopPropagation();
                  inputRef.current?.click();
                }}
              >
                Elegir archivo
              </Button>
              <input
                ref={inputRef}
                hidden
                type="file"
                accept=".xlsx,.csv"
                onChange={(event) => readFile(event.target.files?.[0] || null)}
              />
              {file ? <Typography sx={{ mt: 1, fontSize: ".78rem", color: "text.secondary" }}>{file.name}</Typography> : null}
            </Paper>
          ) : null}

          {preview && !result ? (
            <>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Metric label="Listos" value={summary.ready || 0} />
                <Metric label="Ingresos" value={summary.income || 0} />
                <Metric label="Gastos" value={summary.expense || 0} />
                <Metric label="Transferencias" value={summary.transfer || 0} />
                <Metric label="Ya existentes" value={summary.duplicates || 0} />
                <Metric label="Errores" value={summary.blockingErrors || 0} tone={summary.blockingErrors ? "warn" : "default"} />
              </Stack>

              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.2}>
                  <Box>
                    <Typography sx={{ fontWeight: 950 }}>Período detectado</Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: ".82rem", fontWeight: 750 }}>{dateLabel(summary.from)} → {dateLabel(summary.to)}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 280 }}>
                    <Typography sx={{ fontWeight: 950 }}>Cuentas</Typography>
                    <Stack direction="row" gap={.6} flexWrap="wrap" sx={{ mt: .45 }}>
                      {(preview.accounts?.reuse || []).map((account) => <Chip key={`reuse-${account.name}-${account.currency}`} size="small" label={`${account.name} · ${account.currency}`} />)}
                      {(preview.accounts?.create || []).map((account) => <Chip key={`new-${account.name}-${account.currency}`} size="small" color="primary" variant="outlined" label={`Crear ${account.name} · ${account.currency}`} />)}
                    </Stack>
                  </Box>
                </Stack>
              </Paper>

              {commitState ? (
                <Alert severity={commitState.severity}>
                  <Typography sx={{ fontWeight: 1000 }}>{commitState.title}</Typography>
                  <Typography sx={{ mt: .2, fontSize: ".82rem", fontWeight: 700 }}>{commitState.detail}</Typography>
                </Alert>
              ) : null}

              {(preview.warnings || []).map((warning) => <Alert key={warning} severity="info">{warning}</Alert>)}
              {(preview.errors || []).length ? (
                <Alert severity="error" icon={<WarningAmberRoundedIcon />}>
                  <Typography sx={{ fontWeight: 950 }}>Hay filas que bloquean el import.</Typography>
                  {(preview.errors || []).slice(0, 8).map((row) => <Typography key={`${row.sourceRowNumber}-${row.code}`} sx={{ mt: .35, fontSize: ".8rem" }}>{row.message}</Typography>)}
                </Alert>
              ) : null}

              <Box>
                <Typography sx={{ fontWeight: 1000, mb: .7 }}>Muestra de lo que va a entrar</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 340 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow><TableCell>Fecha</TableCell><TableCell>Tipo</TableCell><TableCell>Cuenta</TableCell><TableCell>Concepto</TableCell><TableCell align="right">Importe contable</TableCell><TableCell>Original</TableCell></TableRow></TableHead>
                    <TableBody>
                      {(preview.sampleRows || []).map((row) => (
                        <TableRow key={row.externalKey} sx={{ opacity: row.duplicate ? .5 : 1 }}>
                          <TableCell>{new Date(row.occurredAt).toLocaleDateString("es-AR")}</TableCell>
                          <TableCell><Chip size="small" label={row.type} /></TableCell>
                          <TableCell>{row.type === "transfer" ? `${row.accountName} → ${row.destinationAccountName}` : row.accountName}</TableCell>
                          <TableCell>{row.category || row.description || "—"}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900 }}>{money(row.amount, row.currency)}</TableCell>
                          <TableCell>{row.originalAmount ? money(row.originalAmount, row.originalCurrency) : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          ) : null}

          {result ? (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack alignItems="center" textAlign="center" spacing={1}>
                <CheckCircleRoundedIcon color="success" sx={{ fontSize: 50 }} />
                <Typography sx={{ fontWeight: 1000, fontSize: "1.25rem" }}>Historial incorporado</Typography>
                <Typography sx={{ color: "text.secondary", fontWeight: 750 }}>
                  {result.movementsCreated} movimientos creados · {result.duplicatesSkipped} repetidos evitados · {result.accountsCreated} cuentas nuevas.
                </Typography>
                <Alert severity="warning" sx={{ mt: 1, textAlign: "left", width: "100%" }}>
                  El historial ya está adentro, pero todavía no afirma que tus saldos actuales sean correctos. El siguiente paso es conciliarlos contra las billeteras/cajas reales.
                </Alert>
              </Stack>
            </Paper>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.6 }}>
        {preview && !result ? <Button disabled={busy} onClick={() => { setPreview(null); setImportId(""); setFile(null); setError(""); }} sx={{ mr: "auto", textTransform: "none", fontWeight: 850 }}>Elegir otro archivo</Button> : <Box sx={{ mr: "auto" }} />}
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none", fontWeight: 850 }}>{result ? "Cerrar" : "Cancelar"}</Button>
        {preview && !result ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {!canCommit ? (
              <Typography sx={{ color: "text.secondary", fontSize: ".78rem", fontWeight: 800 }}>
                {blockingErrorCount > 0
                  ? `${blockingErrorCount} error${blockingErrorCount === 1 ? "" : "es"} pendiente${blockingErrorCount === 1 ? "" : "s"}`
                  : readyCount <= 0 && duplicateCount > 0
                    ? "Sin movimientos nuevos"
                    : readyCount <= 0
                      ? "Nada para importar"
                      : "Preparando confirmación…"}
              </Typography>
            ) : null}
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={!canCommit}
              onClick={commit}
              sx={{ textTransform: "none", fontWeight: 1000, minWidth: 220, boxShadow: canCommit ? 3 : 0 }}
            >
              {canCommit ? `Importar ${readyCount} movimientos` : `Confirmar ${readyCount} movimientos`}
            </Button>
          </Stack>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
